import React, { memo } from 'react';
import moment from 'moment';
import { Formik } from 'formik';
import { has, isEmpty } from 'lodash';
import { TextField } from '@material-ui/core';

import Dialog from 'components/ui/Dialog';
import { createAppointment } from 'api/appointment';
import { useSnackBar } from 'context/SnackBarContext';
import { revertPhoneMask } from 'helpers/revert-phone-mask';
import { FormSectionTitle, GridContainer } from 'styles/form';
import GeneralInfoFormFields from 'components/general/GeneralInfoFormFields';
import Select from 'components/ui/Select';
import Form from 'components/ui/Form';
import { initialValues, validationSchema } from './appointment.config';

const AddAppointmentModal = ({
  appointmentModalOpen,
  setAppointmentModalOpen,
  clinic,
  slots,
  fetchAppointments,
  fetchClinic,
}) => {
  const { setMessage, setOpenSnackBar, setSeverity } = useSnackBar();

  const { id: clinicId, schedule } = clinic || {};
  const { show_birth_date: showBirthDate, custom_field: customField } =
    schedule || {};

  const handleModalClose = () => {
    setAppointmentModalOpen(false);
  };

  const handleDelete = async () => {
    try {
      setMessage('Sub location deleted');
      setSeverity('success');
    } catch (error) {
      console.error(error);
      setMessage('Something went wrong. Failed to delete organization.');
      setSeverity('error');
    }

    setOpenSnackBar(true);
    handleModalClose();
  };

  const handleSave = async (values) => {
    const {
      firstName,
      lastName,
      email,
      phone,
      timeSlot,
      dateOfBirth,
      customFieldValue,
    } = values;

    delete timeSlot.status;

    try {
      await createAppointment(
        {
          first_name: firstName,
          last_name: lastName,
          email,
          phone: revertPhoneMask(phone),
          custom_field_value: customFieldValue,
          ...(dateOfBirth
            ? { date_of_birth: moment(dateOfBirth).format('YYYY/MM/DD') }
            : {}),
        },
        {
          ...timeSlot,
          clinic_id: clinicId,
        },
      );

      setMessage('Appointment created.');
      setSeverity('success');
      fetchAppointments();
      fetchClinic();
    } catch (error) {
      setMessage('Something went wrong. Failed to save appointment.');
      setSeverity('error');
    }

    setOpenSnackBar(true);
    handleModalClose();
  };

  return (
    <Formik
      initialValues={initialValues(customField)}
      validationSchema={validationSchema(customField)}
      enableReinitialize
      onSubmit={handleSave}
    >
      {({
        values: {
          firstName,
          lastName,
          email,
          phone,
          dateOfBirth,
          date,
          timeSlot,
          customFieldValue,
        },
        setFieldValue,
        errors,
        handleChange,
        submitForm,
        touched,
      }) => (
        <Dialog
          open={appointmentModalOpen}
          handleClose={handleModalClose}
          handleDelete={handleDelete}
          disableTopPadding
          maxWidth="sm"
          typeText="Appointment"
          submitForm={submitForm}
          disableDelete
          buttonText="Add Appointment"
        >
          <FormSectionTitle>Appointment</FormSectionTitle>
          <Form fullWidth onSubmit={submitForm}>
            <GridContainer>
              <GeneralInfoFormFields
                firstName={firstName}
                lastName={lastName}
                email={email}
                dateOfBirth={dateOfBirth}
                phone={phone}
                errors={errors}
                touched={touched}
                handleChange={handleChange}
                setFieldValue={setFieldValue}
                showBirthDate={showBirthDate}
              />

              {customField && (
                <TextField
                  color="primary"
                  error={touched.customField && has(errors, 'customFieldValue')}
                  fullWidth
                  helperText={
                    touched.customFieldValue && errors.customFieldValue
                  }
                  label={customField}
                  name="customFieldValue"
                  onChange={handleChange}
                  value={customFieldValue}
                  type="customFieldValue"
                  variant="outlined"
                  autoComplete="chrome-off"
                  required
                />
              )}

              {slots && (
                <Select
                  options={Object.keys(slots).map((slot) => {
                    if (isEmpty(slots)) {
                      return;
                    }

                    return {
                      label: moment(slot).format('ddd - MMM DD, YYYY'),
                      value: slot,
                    };
                  })}
                  label="Available Dates"
                  variant="outlined"
                  errors={touched.date && has(errors, 'date')}
                  touched={touched}
                  helperText={touched.date && errors.date}
                  name="date"
                  onChange={handleChange}
                  value={date}
                  disableNone
                  required
                />
              )}

              {!isEmpty(date) && (
                <Select
                  options={slots[date]
                    .filter(({ status }) => status === 'available')
                    .map((slot) => {
                      const { start_date: startDate } = slot;
                      return {
                        label: moment(startDate, 'YYYY-MM-DD hh:mm:ss').format(
                          'h:mma',
                        ),
                        value: slot,
                      };
                    })}
                  label="Available Slots"
                  variant="outlined"
                  errors={touched.timeSlot && has(errors, 'timeSlot')}
                  touched={touched}
                  helperText={touched.timeSlot && errors.timeSlot}
                  name="timeSlot"
                  onChange={handleChange}
                  value={timeSlot}
                  disableNone
                  required
                />
              )}
            </GridContainer>
          </Form>
        </Dialog>
      )}
    </Formik>
  );
};

export default memo(AddAppointmentModal);
